/**
 * Shared look-and-feel engine for white-label partner surfaces.
 * A partner picks ONE accent hex and a light/dark theme; every supporting tone,
 * overlay and text colour is derived from those two so any hue stays legible.
 */
export const rgb = (hex) => {
  const h = (hex || "#4060FF").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const alpha = (hex, a) => { const [r, g, b] = rgb(hex); return `rgba(${r},${g},${b},${a})`; };

export const mix = (hex, target, amt) => {
  const [r, g, b] = rgb(hex); const [tr, tg, tb] = rgb(target);
  const m = (x, t) => Math.round(x + (t - x) * amt);
  return `rgb(${m(r, tr)},${m(g, tg)},${m(b, tb)})`;
};

export const mixHex = (hex, target, amt) => {
  const [r, g, b] = rgb(hex); const [tr, tg, tb] = rgb(target);
  const m = (x, t) => Math.round(x + (t - x) * amt).toString(16).padStart(2, "0");
  return `#${m(r, tr)}${m(g, tg)}${m(b, tb)}`;
};

// WCAG relative luminance.
export const lumOf = (hex) => {
  const [r, g, b] = rgb(hex).map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// A partner is free to pick any hex, including white or black — which would make
// every button and band on their portal disappear into the page. Nudge the
// extremes back towards something that still reads as a colour on their theme.
export const safeAccent = (hex, theme) => {
  const L = lumOf(hex || "#4060FF");
  if (theme === "dark") return L < 0.05 ? mixHex(hex, "#FFFFFF", 0.42) : hex;
  return L > 0.78 ? mixHex(hex, "#0F1226", 0.62) : hex;
};

// WCAG relative luminance, so text over the accent is never unreadable.
export const readableOn = (hex) => (lumOf(hex) > 0.42 ? "#0F1226" : "#FFFFFF");

export const THEME = {
  light: {
    pageBg: "linear-gradient(180deg,#F8FAFC 0%,#EFF3F8 100%)",
    card: "rgba(255,255,255,0.86)", cardStrong: "rgba(255,255,255,0.97)",
    border: "rgba(15,23,42,0.09)", ink: "#0F172A", ink2: "#475569", ink3: "#8C9BB0",
    inputBg: "#FFFFFF", headerBg: "rgba(248,250,252,0.86)", band: "rgba(15,23,42,0.03)",
    scrim: "linear-gradient(100deg, rgba(252,253,255,0.97) 0%, rgba(252,253,255,0.86) 44%, rgba(252,253,255,0.38) 100%), linear-gradient(to top, rgba(252,253,255,0.94) 0%, rgba(252,253,255,0) 58%)",
  },
  dark: {
    pageBg: "linear-gradient(180deg,#090D16 0%,#0E1524 100%)",
    card: "rgba(19,27,43,0.74)", cardStrong: "rgba(26,36,56,0.9)",
    border: "rgba(255,255,255,0.11)", ink: "#F6F9FC", ink2: "#C2CFDF", ink3: "#7E8EA5",
    inputBg: "rgba(255,255,255,0.05)", headerBg: "rgba(9,13,22,0.8)", band: "rgba(255,255,255,0.03)",
    scrim: "linear-gradient(100deg, rgba(7,10,18,0.96) 0%, rgba(7,10,18,0.8) 44%, rgba(7,10,18,0.34) 100%), linear-gradient(to top, rgba(7,10,18,0.94) 0%, rgba(7,10,18,0) 58%)",
  },
};

export const themeOf = (name) => THEME[name === "dark" ? "dark" : "light"];

// Hero background the portal should render: the partner's own upload wins,
// otherwise one of the built-in stock backgrounds.
export const heroSrcOf = (brand) => brand?.hero_image || (brand?.hero_preset ? `/portal/${brand.hero_preset}.jpg` : null);

export const PLAIN_HERO = (accent) =>
  `linear-gradient(120deg, ${mix(accent, "#000000", 0.28)} 0%, ${accent} 58%, ${mix(accent, "#FFFFFF", 0.22)} 100%)`;
